import {
  fetchData, ModelGroupedBase, ModelUngroupedBase, addCombinedProperties, Schema, Number, Integer, Boolean, String, Date, Any,
  List as SchemaList, Map as SchemaMap
} from '@artsman/ams-loader';

import { DefaultCollection } from './collections';
import { getEndpointAndIncludes } from './loading';

function getSchemaUrl(endpointAndIncludes) {
  let { endpoint, includes } = getEndpointAndIncludes(endpointAndIncludes);
  const query = includes.length ? '?include=' + includes.join('+') : '';
  return endpoint + '/schema' + query;
}

function getGroupSchemaUrl(schemaUrl, group) {
  if (group !== undefined) {
    return schemaUrl + '?' + group;
  }
  else {
    return schemaUrl;
  }
}

/*
 * grouped models should use only= but ams-loader currently only supports 1 model per group string, so it needs to be disabled for now.
 */
const USE_ONLY = false;

/*
 * until only= is supported for grouped models in ams-loader, use a hack to instead filter out undefined values when generating the group key
 * if values are undefined, we assume they have been filtered out due to groups being loaded with only= present.
 */
function generateFilteredGroupKey(groupKeys, groupKeySeparator, item) {
  groupKeys = groupKeys.filter(groupKey => item[groupKey] !== void 0);
  if (groupKeys.length === 1) {
    return item[groupKeys[0]]; // make sure to NOT join to preserve type if there's only one key!
  }
  else {
    return groupKeys.map(groupKey => item[groupKey]).join(groupKeySeparator);
  }
}


const ONLY_FIELD_WRAPPER_TEXT = '%22';

function wrapOnlyField(onlyField) {
  return ONLY_FIELD_WRAPPER_TEXT + onlyField + ONLY_FIELD_WRAPPER_TEXT;
}

const RELATED_SEPARATOR = '.';
const RELATED_SEPARATOR_REGEXP = /[\.]/g;
const RELATED_SEPARATOR_REPLACEMENT = '_';

function replaceSeparators(onlyField) {
  return onlyField.replace(RELATED_SEPARATOR_REGEXP, RELATED_SEPARATOR_REPLACEMENT);
}

function hasSeparator(onlyField) {
  return onlyField.indexOf(RELATED_SEPARATOR) !== -1;
}

function stripSeparator(onlyField) {
  return onlyField.substring(0, onlyField.lastIndexOf(RELATED_SEPARATOR));
}

function getOnlySchemaUrl(groupSchemaUrl, only) {
  if (USE_ONLY && only) {
    const joinCharacter = groupSchemaUrl.indexOf('?') !== -1 ? '&' : '?';

    let relationshipMap = {};
    let relatedOnlyFields = only.filter(hasSeparator);
    for (let relatedOnlyField of relatedOnlyFields) {
      relationshipMap[stripSeparator(relatedOnlyField)] = true;
    }
    let relationships = Object.keys(relationshipMap);

    only = only.map(replaceSeparators);

    let include = '';
    if (relationships.length > 0) {
      include = '&include=' + relationships.join('+');
    }

    return groupSchemaUrl + joinCharacter + 'q=field:[' + only.map(wrapOnlyField).join(',') + ']' + include;
  }
  return groupSchemaUrl;
}

const loadedTypeToSchemaTypeMap = {
  'Timestamp Local': Date,
  'Timestamp Local (No Timezone)': Date,
  'Timestamp UTC': Date,
  'Date': Date,
  'Time': Date,
  'Integer ID': Integer,
  'Integer ID - Parent': Integer,
  'Integer': Integer,
  'Float': Number,
  'Decimal': Number,
  'Currency': Number,
  'String ID': String,
  'String ID - Parent': String,
  'String': String,
  'ETag': String,
  'Function': String,
  'Boolean': Boolean,
  'Array': () => SchemaList(Any()),
  'Map': () => SchemaMap(Any(), Any()),
  'Set<Integer>': () => SchemaList(Integer()), // TODO add other Set / Map subtypes...
  'URL': String,
  'URL - ID': String,
  'URL - Public': String,
  'URL - Parent': String,
  'URL - Children': String
};

const loadedTypeToSchemaUniqueMap = {
  'Integer ID': true,
  'String ID': true,
};

function parseAggregateSchema(groupSchemaData) {
  let schemaFields = {};

  let groupFields = [];
  groupSchemaData.forEach(groupSchemaField => {
    let { field, type, is_group } = groupSchemaField;
    if (field !== undefined && type !== undefined && is_group !== undefined) {
      let schemaType = loadedTypeToSchemaTypeMap[type];
      if (schemaType !== undefined) {
        schemaFields[field] = schemaType();
        if (is_group === true) {
          groupFields.push(field);
        }
      }
      else {
        throw new Error('group schema field had unrecognized type, field: ' + field + ' type: ' + type + ' is_group: ' + is_group);
      }
    }
    else {
      throw new Error('group schema field was malformed, field: ' + field + ' type: ' + type + ' is_group: ' + is_group);
    }
  });
  return {
    schemaFields,
    groupFields
  };
}

function parseSchema(schemaData) {
  let schemaFields = {};

  let uniqueKey = null;
  schemaData.forEach(schemaField => {
    let { field, type } = schemaField;
    if (field !== undefined && type !== undefined) {
      let schemaType = loadedTypeToSchemaTypeMap[type];
      if (schemaType !== undefined) {
        schemaFields[field] = schemaType();
        if (loadedTypeToSchemaUniqueMap[type]) {
          if (uniqueKey !== null) {
            throw new Error('schema field duplicate unique key found: ' + field + ' (previous: ' + uniqueKey + ')');
          }
          uniqueKey = field;
        }
      }
      else {
        throw new Error('schema field had unrecognized type, field: ' + field + ' type: ' + type);
      }
    }
    else {
      throw new Error('schema field was malformed, field: ' + field + ' type: ' + type);
    }
  });
  if (uniqueKey === null) {
    throw new Error('schema was missing unique key field!');
  }

  return {
    schemaFields,
    uniqueKey
  }
}

export function getSchemaModelLoader(dataSource, declaration, requestOptions, getEndpoint) {
  let {
    getAuthenticationHeaders,
    onRequest,
    onReceive: onModelReceive,
    onReceiveUnchanged: onModelReceiveUnchanged,
    onReceiveUnauthenticated,
    onReceiveUnauthorized,
    onError,
    group,
    only
  } = requestOptions;

  let schemaUrl = getSchemaUrl(getEndpoint({ dataSource }));
  let groupSchemaUrl = getGroupSchemaUrl(schemaUrl, group);
  let groupFilterSchemaUrl = getOnlySchemaUrl(groupSchemaUrl, only);
  let many = true;
  let options = {};

  let getchemaDataReceived = (onSchemaLoaded, onSchemaError) =>
    (data) => {
      try {
        let SchemaModel;
        if (group !== undefined) {
          let { schemaFields, groupFields } = parseAggregateSchema(data);
          let schema = Schema(schemaFields);

          @addCombinedProperties
          class GroupedSchemaModel extends ModelGroupedBase {
            static get modelName() {
              return dataSource + '-' + group;
            }

            static get schema() {
              return schema;
            }

            static get groupKeys() {
              return groupFields;
            }

            static get CollectionClass() {
              return DefaultCollection;
            }
          }
          if (!USE_ONLY) {
            GroupedSchemaModel.generateGroupKey = function(item) {
              return generateFilteredGroupKey(this.groupKeys, this.groupKeySeparator, item);
            };
          }
          SchemaModel = GroupedSchemaModel;
        }
        else {
          let { schemaFields, uniqueKey } = parseSchema(data);
          let schema = Schema(schemaFields);

          @addCombinedProperties
          class UngroupedSchemaModel extends ModelUngroupedBase {
            static get modelName() {
              return dataSource;
            }

            static get schema() {
              return schema;
            }

            static get uniqueKey() {
              return uniqueKey;
            }

            static get CollectionClass() {
              return DefaultCollection;
            }
          }
          SchemaModel = UngroupedSchemaModel;
        }
        onSchemaLoaded(SchemaModel);
      }
      catch (error) {
        onSchemaError(error);
      }
    };

  let fetchSchemaOptions = {
    getAuthenticationHeaders,
    onRequest,
    onReceive: getchemaDataReceived(onModelReceive, onError),
    onReceiveUnchanged: getchemaDataReceived(onModelReceiveUnchanged, onError),
    onReceiveUnauthenticated,
    onReceiveUnauthorized,
    onError
  };

  return fetchData(groupFilterSchemaUrl, many, options, fetchSchemaOptions);
}